import React, { useEffect, useState, useCallback } from 'react';
import DashboardController from '../Controllers/DashboardController';

const PAGE_SIZE = 25;

export default function AuditLogs() {
  const [filters, setFilters] = useState({ action: '', status: '', from: '', to: '' });
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (filters.action) params.action = filters.action;
      if (filters.status) params.status = filters.status;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;

      const data = await DashboardController.getAuditLogs(params);
      setResult(data);
    } catch (err) {
      setError('Failed to load audit logs. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (field) => (e) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const totalPages = Math.max(1, Math.ceil((result.total || 0) / PAGE_SIZE));

  return (
    <div className="main-panel">
      <div className="content-wrapper ivs-page">
        <div className="d-flex align-items-start justify-content-between mb-4 flex-wrap gap-2">
          <div className="text-left">
            <h2 className="mb-1 font-weight-bold text-left page-heading">Audit Logs</h2>
            <p className="text-muted m-0 text-left" style={{ fontSize: 15 }}>
              System-wide event trail
            </p>
          </div>
        </div>

        <form className="ivs-search-panel" onSubmit={handleSearch}>
          <div className="ivs-search-row" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr auto' }}>
            <div>
              <label className="ivs-field-label">Action</label>
              <input
                className="ivs-field"
                placeholder="e.g. LOGIN_SUCCESS"
                value={filters.action}
                onChange={handleFilterChange('action')}
              />
            </div>

            <div>
              <label className="ivs-field-label">Status</label>
              <select className="ivs-field" value={filters.status} onChange={handleFilterChange('status')}>
                <option value="">All Status</option>
                <option value="SUCCESS">SUCCESS</option>
                <option value="FAILED">FAILED</option>
              </select>
            </div>

            <div>
              <label className="ivs-field-label">From</label>
              <input type="date" className="ivs-field" value={filters.from} onChange={handleFilterChange('from')} />
            </div>

            <div>
              <label className="ivs-field-label">To</label>
              <input type="date" className="ivs-field" value={filters.to} onChange={handleFilterChange('to')} />
            </div>

            <button className="ivs-btn-primary" type="submit" disabled={loading}>
              {loading ? 'Loading…' : 'Search'}
            </button>
          </div>
        </form>

        {error && (
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="bi bi-exclamation-triangle mr-2"></i>
            {error}
          </div>
        )}

        <div className="ivs-panel">
          <table className="table table-striped mb-0">
            <thead>
              <tr>
                <th>Time</th>
                <th>Event</th>
                <th>Actor</th>
                <th>Company</th>
                <th>Target</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {result.data.length > 0 ? (
                result.data.map((log) => (
                  <tr key={log.id}>
                    <td>{new Date(log.createdAt).toLocaleString()}</td>
                    <td>{log.event}</td>
                    <td>{log.actor || '—'}</td>
                    <td>{log.company || '—'}</td>
                    <td>{log.target || '—'}</td>
                    <td>{log.ipAddress || '—'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    {loading ? 'Loading…' : 'No audit events found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {result.total > 0 && (
            <div className="ivs-pagination">
              <button
                className="ivs-pagination__btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="ivs-pagination__label">
                Page {page} of {totalPages} · {result.total} events
              </span>
              <button
                className="ivs-pagination__btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
